import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { LoadingSpinnerIcon } from '@/atoms/Icons/LoadingSpinnerIcon'
import { MagnifyingGlassIcon } from '@/atoms/Icons/MagnifyingGlassIcon'
import { ScreenType, useScreenType } from '@/utils/ScreenUtils'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import useAutoCompleteAddressSearch from '@/views/TicketCheckoutViews/ShowtimesView/hooks/useAutoCompleteAddressSearch'
import '@/views/TicketCheckoutViews/ShowtimesView/hooks/usePreventFacebookAutofill'
import { Coordinates } from '@/views/TicketCheckoutViews/ShowtimesView/hooks/useVenueSearch/useVenueSearch'

interface NewAddressSearchProps {
  onChange: (coordinates: Coordinates, searchString: string) => void
  className?: string
  countryCode: string
  scrollToVenues: () => void
  scrollToContentTop: () => void
  defaultAddressString?: string
}

export const AddressSearch: React.FC<NewAddressSearchProps> = ({
  onChange,
  className,
  countryCode,
  defaultAddressString,
  scrollToVenues,
  scrollToContentTop,
}) => {
  const { query, isReady } = useSafeRouter()
  const screenType = useScreenType()
  const { search, manualSearch, results, clearResults, loading } = useAutoCompleteAddressSearch({
    countryCode,
  })
  const [inputValue, setInputValue] = useState<string>(defaultAddressString ?? '')
  const [hoverValue, setHoverValue] = useState<number>(0)
  const [showPredictionPanel, setShowPredictionPanel] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [hasSetDefaultInputValue, setHasSetDefaultInputValue] = useState(false)
  const inputWrapperRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const { t } = useTranslate('tickets')

  const coordinates = useMemo(() => {
    return results[hoverValue]?.geometry.coordinates
  }, [results, hoverValue])

  const clearSearch = useCallback(() => {
    setInputValue('')
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    if (defaultAddressString && !hasSearched) {
      setInputValue(defaultAddressString)
      setHasSetDefaultInputValue(true)
    }
  }, [defaultAddressString, hasSearched, hasSetDefaultInputValue, isReady, query.city, query.searchDate])

  const handleChange = useCallback(
    (coordinates: Coordinates, searchString: string) => {
      onChange(coordinates, searchString)
      scrollToVenues()
    },
    [onChange, scrollToVenues],
  )

  const handleInputChange = (value: string) => {
    setInputValue(value)
    search(value)
  }

  useEffect(() => {
    const closePredictionPanelOnClick = (e: MouseEvent) => {
      if (inputWrapperRef.current && !inputWrapperRef.current.contains(e.target as Node)) {
        setShowPredictionPanel(false)
      }
    }
    document.addEventListener('click', closePredictionPanelOnClick)

    return () => {
      document.removeEventListener('click', closePredictionPanelOnClick)
    }
  }, [])

  useEffect(() => {
    if (hoverValue > results.length) setHoverValue(0)
  }, [hoverValue, results.length])

  useEffect(() => {
    if (inputValue === '') clearResults()
  }, [clearResults, inputValue])

  return (
    <div
      onClick={() => {
        if (screenType !== ScreenType.desktop) {
          scrollToContentTop()
        }
        if (!hasSearched) {
          setHasSearched(true)
          setInputValue('')
        }
      }}
      ref={inputWrapperRef}
      role="search"
      className={classNames('relative z-[40] h-full w-full', className)}
    >
      <div
        aria-label="input"
        className={classNames(
          'absolute inset-x-0 top-0 z-10 overflow-hidden rounded-[10px] border-core-gray-400 border-[1.5px] bg-white ',
          showPredictionPanel && results?.length > 0 && 'shadow-light-3',
        )}
      >
        <div className="relative">
          <input
            ref={inputRef}
            id="location-search"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            type="text"
            onClick={() => setShowPredictionPanel(true)}
            className="w-full rounded-md bg-transparent p-2 px-11 text-core-gray-950 ring-0 ring-transparent placeholder:text-core-gray-600"
            value={inputValue}
            list="address-list"
            placeholder={t('geoapifyInputPlaceholder', 'Search City, Business, Or Zip Code')}
            onChange={(evt) => {
              handleInputChange(evt.target.value)
              setShowPredictionPanel(true)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (results.length > 0) {
                  if (!showPredictionPanel || !coordinates) return
                  setInputValue(results[hoverValue]?.properties?.label ?? '')
                  handleChange(coordinates, results[hoverValue]?.properties?.label ?? '')
                  setShowPredictionPanel(false)
                }

                if (results.length === 0 && !loading) {
                  manualSearch(inputValue)
                }
              }
              if (e.key === 'ArrowDown') {
                setHoverValue((state) => {
                  if (state > results.length) return state
                  return state + 1
                })
              }

              if (e.key === 'ArrowUp') {
                setHoverValue((state) => {
                  if (state === 0) return state
                  return state - 1
                })
              }
            }}
          />
          {loading ? (
            <div className="absolute left-4 top-1/2 -translate-y-1/2">
              <LoadingSpinnerIcon size={20} color="core-gray-500" />
            </div>
          ) : (
            <MagnifyingGlassIcon size={20} className="absolute left-4 top-1/2 -translate-y-1/2" color="core-gray-500" />
          )}

          {inputValue && (
            <button onClick={clearSearch} className="absolute right-4 top-1/2 -translate-y-1/2">
              <CloseIcon color="core-gray-500" size={20} />
            </button>
          )}
        </div>
        {showPredictionPanel && (
          <ul role="listbox" className="bg-transparent">
            {results.length > 0 &&
              results.map((result, index) => {
                const isLast = index + 1 === results.length

                return (
                  <li
                    key={result?.properties?.id}
                    tabIndex={0}
                    role="option"
                    aria-selected={hoverValue === index}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleChange(result.geometry.coordinates, result?.properties?.label)
                        setInputValue(result?.properties?.label)
                        setShowPredictionPanel(false)
                      }
                    }}
                    onMouseEnter={() => {
                      setHoverValue(index)
                    }}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleChange(result.geometry.coordinates, result?.properties?.label)
                      setInputValue(result?.properties?.label)
                      setShowPredictionPanel(false)
                    }}
                    className={classNames(
                      'cursor-pointer px-2 pl-10 py-[10px] flex gap-2 items-center hover:bg-core-gray-200',
                      index === hoverValue && 'bg-core-gray-200',
                      isLast && 'mb-2',
                    )}
                  >
                    {result?.properties?.label}
                  </li>
                )
              })}
          </ul>
        )}
      </div>
    </div>
  )
}
